/*
 * Copyright (c) 2020 Honeywell International Inc. All Rights Reserved.
 * Honeywell and its product names are among the trademarks and/or service marks owned
 * by Honeywell International, Inc., or its subsidiaries.
 *
 * For patent information, see http://www.hsmpats.com.
 */

import React, { Component } from 'react';

/**
 * Class that renders the SLA text presented to the end user.
 */
class SLAText extends Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  render() {
    return (
      <>
        <center>
          <b>VOCOLLECT SOFTWARE LICENSE AGREEMENT</b>
        </center>
        <br />
        <p>
          This Software License Agreement (&quot;Agreement&quot;) is by and between Hand Held
          Products, Inc., acting through its Vocollect line of business (&quot;Vocollect&quot;) and
          the undersigned licensee (&quot;Licensee&quot; or “Customer”) (each a “Party” or
          collectively, the “Parties). Intending to be legally bound, Vocollect and Licensee agree
          as follows:
        </p>
        <ol>
          <li>
            <b>
              <u>Definitions.</u>
            </b>{' '}
            For purposes of this Agreement,
            <ol type="a">
              <li>
                <b>&quot;Affiliate&quot;</b> means any entity controlled by, or under common control
                with, Licensee, and any reference to control shall mean ownership of more than fifty
                percent (50%) of the outstanding voting stock of an entity or the equivalent
                authority to direct the legal affairs of an entity.
              </li>
              <li>
                <b>“Applicable Data Privacy Laws”</b> means applicable data protection, privacy,
                breach notification, or data security laws or regulations.
              </li>
              <li>
                <b>“Licensee Personal Data”</b> means Personal Data received by Vocollect from or on
                behalf of Customer in connection with Vocollect’s provision of the Software.
              </li>
              <li>
                <b>“Personal Data”</b> is any information relating to an identified or identifiable
                natural person; an identifiable natural person is one who can be identified,
                directly or indirectly, in particular by reference to an identifier such as a name,
                an identification number, location data, an online identifier or to one or more
                factors specific to the physical, physiological, genetic, mental, economic, cultural
                or social identity of that natural person, or as that term (or similar variants) may
                otherwise be defined in Applicable Data Privacy Laws.
              </li>
              <li>
                <b>“Software”</b> means:
                <ol type="i">
                  <li>
                    any Vocollect software received by Licensee from Vocollect or from an authorized
                    reseller of Vocollect;
                  </li>
                  <li>
                    all Tasks, VoiceApplications, modifications, enhancements, new versions, and new
                    releases related to the Vocollect software provided by Vocollect or its
                    authorized representative; and
                  </li>
                  <li>any copies of the above.</li>
                </ol>
              </li>
              <li>
                <b>“Tasks”</b> means voice applications built by Vocollect with TaskBuilder Software
              </li>
              <li>
                <b>“VoiceApplications”</b> means voice applications built by Vocollect with
                VoiceArtisan Software.
              </li>
            </ol>
          </li>
          <li>
            <b>
              <u>License.</u>
            </b>
            <ol type="a">
              <li>
                Vocollect hereby grants Licensee a limited, non-exclusive, non-transferable license
                to the Software in object code, solely for use in accordance with and during the
                term of this Agreement (the &quot;License&quot;). Licensee&apos;s use of the
                Software is restricted to the number of users for whose use Licensee has paid. A
                description of the types of licenses available from Vocollect is set forth in
                Section 2.e. below.
              </li>
              <li>
                Licensee represents that it is acting for itself and for any Affiliates listed in
                Exhibit A. Affiliates listed in Exhibit A may purchase and use the Software
                according to the terms of this Agreement. Licensee shall inform each Affiliate of
                the existence of this Agreement and their obligation to abide by the terms of this
                Agreement. Licensee warrants and represents that it has the legal authority to bind
                the Affiliates, and agrees to be responsible to Licensor for the breach by any
                Affiliate of the terms of this Agreement.
              </li>
              <li>
                Vocollect and its licensors and/or suppliers, as applicable, shall retain all right,
                title, and interest in and to the Software, including all patents, copyrights,
                trademarks, trade secret, know-how, and other proprietary rights thereto. Licensee
                may not make copies of the Software except as reasonably required for standard
                archival or backup purposes. Absent prior written consent from Vocollect, Licensee
                will not, nor will Licensee authorize any third party to, (i) translate or localize
                the Software; (ii) distribute, sell, lend, rent, transfer, or convey the Software;
                or (iii) grant any sublicense, lease, or other rights in the Software to any third
                party. Further, Licensee will not, nor will Licensee authorize or knowingly allow
                any third party to, (iv) decompile, disassemble, reverse engineer, or otherwise
                attempt to reconstruct, identify, or discover any source code, underlying user
                interface architecture or techniques, or algorithms of the Software by any means
                whatsoever and for any purpose whatsoever, or (v) take any action, or knowingly
                allow any third party to take any action, that would cause the Software or any
                portion of it to be placed in the public domain. Any proprietary rights notices
                present in the Software, including but not limited to patent and copyright notices,
                must not be deleted, obscured, or otherwise altered in any way, and must be fully
                and accurately included in any copies.
              </li>
              <li>
                The Software may include or be distributed with third party software, including open
                source software (&quot;Third-Party Software&quot;). Use of Third-Party Software is
                subject to the terms of this Agreement as well as any separate license terms
                accompanying such Third-Party Software. Licensee acknowledges that it is solely
                responsible for complying with any applicable licenses for Third-Party Software.
              </li>
              <li>
                The Software may be licensed by client licenses, site/enterprise licenses,
                subscription, and/or for use on a particular hardware device. Software licensed for
                use on a specific hardware device may be transferred to another hardware device to
                remedy failure of the initial hardware device with prior written notice to Licensor,
                but not otherwise.
              </li>
              <li>
                Notwithstanding anything to the contrary herein, Licensee may permit its third-party
                contractors and consultants (including outsourcing service providers) to use the
                Software solely to provide services to Licensee; however, use of the Software by a
                third party in the course of providing repair services to Licensee repair is
                expressly prohibited unless authorized in writing by Vocollect. Licensee shall be
                responsible and liable to Vocollect for any breach of the terms of this Agreement by
                any third-party contractors and consultants (including outsourcing service
                providers).
              </li>
              <li>
                Certain Vocollect products may include Software that collects information about how,
                and under what conditions, the Vocollect product is used and functions. This
                information may include, but is not limited to, information regarding user input
                (such as touch panel, keyboard, and trigger use), power and power management (such
                as battery level and status and recharge time) docking events, system up and down
                time, backlighting use, voice/audio capture, information from any sensors (such as
                location, motion, temperature, and ambient lighting), and use with peripherals. In
                addition, Vocollect may collect voice recordings from Vocollect productsfor the
                purpose of improving product performance.
              </li>
              <li>
                Data Rights: Licensee retains all rights that it already holds in data and other
                information that it or persons acting in its behalf input, upload, transfer or make
                accessible in relation to, or which is collected from it or third party devices or
                equipment by, the Software, (“Input Data”). Vocollect and its Affiliates have the
                right to retain, transfer, disclose, duplicate, analyze, modify and otherwise use
                Input Data to provide, protect, improve or develop its products or services.
                Vocollect and its Affiliates may also use Input Data for any other purpose provided
                it is in an anonymized form that does not identify the Licensee. Any Licensee
                Personal Data contained within Input Data shall only be used or processed in
                accordance with the data privacy terms of this Agreement and applicable law. All
                information, analysis, insights, inventions and algorithms derived from Input Data
                by Vocollect and/or its affiliates (but excluding the Input Data itself) and any
                intellectual property rights related thereto, are owned exclusively and solely by
                Vocollect and are its confidential information. This Section survives termination of
                this Agreement.
              </li>
            </ol>
          </li>
          <li>
            <b>
              <u>Term and Termination.</u>
            </b>{' '}
            This Agreement will remain in effect unless and until terminated as follows:
            <ol type="a">
              <li>
                <b>At Will:</b> Licensee may terminate this Agreement at will at any time by
                destroying all copies of the Software, purging same from all memory devices, and
                providing Vocollect with written notice.
              </li>
              <li>
                <b>For Cause:</b> if Licensee commits a material breach Vocollect may provide a
                written notice of such breach, and the Agreement will then terminate automatically
                if Licensee does not cure the breach within thirty (30) days after receipt of the
                notice.
              </li>
              <li>
                <b>Immediate Termination:</b> this Agreement will terminate immediately and
                automatically at the end of any term for which subscription fees have been paid
                unless the subscription is timely renewed, or upon any breach of subsections (iv) or
                (v) of Section 2.c.
              </li>
              <li>
                Upon any termination, Licensee will destroy all copies of the Software or, if
                requested by Vocollect, return all copies of the Software to Vocollect; and, if
                requested by Vocollect, provide a corresponding certification from authorized
                representative.
              </li>
            </ol>
          </li>
          <li>
            <b>
              <u>Confidentiality.</u>
            </b>
            <ol type="a">
              <li>
                “Confidential Information” means any information in any form that is disclosed to
                one party (“Recipient”) by or on behalf of the other party (“Discloser”) that is not
                generally known including, without limitation, trade secrets, know how, samples,
                techniques, specifications, drawings, designs design concepts, processes, and
                testing methodologies. Information will not be considered as Confidential
                Information unless (a) marked “CONFIDENTIAL”, “PROPRIETARY” or to similar effect at
                the time of disclosure, or (b) disclosed orally or visually but identified as
                confidential at the time of disclosure and confirmed as confidential in a writing
                sent to Recipient within 30 days of disclosure summarizing the Confidential
                Information sufficiently for identification; further provided, Vocollect SLA US –
                Rev 9/26/19 3 however, that all Software, and the terms and conditions of this
                Agreement, shall be considered Confidential Information of Vocollect whether or not
                so marked.
              </li>
              <li>
                Each party agrees not to disclose the other party&apos;s Confidential Information to
                third parties or use the Confidential Information other than in connection with its
                license rights under this Agreement. Each party will use at least the same security
                measures as it uses to protect its own similar confidential and trade secret
                information, but no less than a reasonable degree of care. Confidential Information
                shall not include information that: (i) was in Recipient’s possession and not
                subject to an obligation of confidentiality before receipt from Discloser, (ii) has
                become or later becomes legally available in the public domain through no fault of
                the Recipient, (iii) was rightfully received by Recipient from a third party who has
                no obligation of confidentiality to the Discloser, (iv) is independently developed
                by Recipient without use of Confidential Information, or (v) is required to be
                disclosed pursuant to law or court order, provided that Recipient shall notify
                Discloser prior to such disclosure and assist Discloser in preventing or limiting
                such required disclosure.
              </li>
              <li>
                The parties agree and acknowledge that any breach of the provisions regarding
                ownership or confidentiality contained in this Agreement may cause the other party
                irreparable harm, and that, in the event of breach or threatened breach of such
                provisions, the non-breaching party may seek injunctive relief without the
                requirement to post a bond as well as seek all other remedies available to it in law
                and in equity.
              </li>
              <li>
                Except for Software and any Confidential Information that qualifies as a trade
                secret under applicable law, each party’s obligation of confidentiality with respect
                to Confidential Information of the other party will continue for five (5) years
                after expiration or termination of this Agreement. The obligation of confidentiality
                for Software shall not expire until and unless the Software becomes legally
                available in the public domain. The obligation of confidentiality for qualifying
                trade secrets shall not expire until and unless they lose such qualification.
              </li>
            </ol>
          </li>
          <li>
            <b>
              <u>Data Privacy</u>
            </b>
            <ol type="a">
              <li>
                Each party may provide each other with certain Personal Data such as name and
                certain business contact details relating to individuals engaged by the other Party
                or its affiliates (“Staff”) for the purposes of performing the obligations under
                this Agreement and managing the business relationship between the Parties
                (“Purposes”).
              </li>
              <li>
                Any processing of Personal Data will be done in accordance with the terms of this
                Agreement and Applicable Data Privacy Laws. Each Party will (a) implement all
                appropriate security measures to protect Personal Data provided by the other Party
                against accidental, unlawful, or unauthorized (i) destruction (ii) loss, (iii)
                alteration, (iv) disclosure, or (v) access (including remote access), (b) protect
                Personal Data provided by the other Party against all other forms of unlawful
                processing, including unnecessary collection, transfer, or processing, beyond what
                is strictly necessary for the Purposes, (c) prior to any transfer of Personal Data,
                impose all obligations on third parties as required by the Purchase Order and
                Applicable Data Privacy Laws, and (d) securely delete such Personal Data once it is
                no longer required for the Purposes.
              </li>
              <li>
                Each Party shall be responsible for providing necessary information and
                notifications required by Applicable Data Privacy Laws to its respective Staff.
              </li>
              <li>
                Where a Party’s Personal Data is transferred to a country that has not been deemed
                to provide an adequate level of protection for personal data by Applicable Data
                Privacy Laws, the other Party will either enter or apply legally recognized
                international data transfer mechanisms, including (a) standard data protection
                clauses adopted or approved by the European Commission; (b) binding corporate rules
                which provide adequate safeguards, or (c) any other similar program or certification
                that is recognised as providing an adequate level of protection in accordance with
                Applicable Data Privacy Laws.
              </li>
              <li>
                Additionally, the attached Data Privacy Exhibit, (Exhibit B) sets forth Vocollect’s
                obligations and responsibilities with respect to processing personal data for the
                provision of the Software.
              </li>
            </ol>
          </li>
          <li>
            <b>
              <u>Limited Warranty and Disclaimer.</u>
            </b>
            <ol type="a">
              <li>
                Provided that Licensee is not in breach of any of Licensee’s obligations under this
                Agreement, Vocollect warrants that the Software will substantially conform to the
                published documentation for the Software for a period of ninety (90) days from the
                date the Software is shipped. Vocollect further warrants that it has used reasonable
                efforts to ensure that all Software furnished hereunder is free of viruses, worms,
                or other malicious code intended to interfere with proper operation of the Software.
              </li>
              <li>
                Licensee’s sole remedy and Vocollect’s sole obligation in the event of breach of
                this warranty, is, at Vocollect’s option, correction of the substantial
                nonconformity or a refund of the applicable license fee for the Software.
              </li>
              <li>
                The above warranty does not apply if and to the extent that:
                <ol type="i">
                  <li>
                    the Software is subjected to misuse or unauthorized use, or modified by a party
                    other than Vocollect;
                  </li>
                  <li>
                    the claim results from acts or omissions caused by persons other than Vocollect,
                    or from hardware, software, or documentation not provided by Vocollect;
                  </li>
                  <li>
                    the claim is not reported to Vocollect within the warranty period or is not
                    reasonably documented by Licensee; or
                  </li>
                  <li>
                    Licensee is not using the most recent release of the Software made available to
                    Licensee by Vocollect.
                  </li>
                </ol>
              </li>
              <li>
                <b>
                  VOCOLLECT DISCLAIMS ALL OTHER WARRANTIES FOR THE SOFTWARE, EXPRESS OR IMPLIED,
                  INCLUDING BUT NOT LIMITED TO THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
                  FOR A PARTICULAR PURPOSE AND NON-INFRINGEMENT AND WARRANTIES ARISING FROM COURSE
                  OF DEALING OR USAGE OF TRADE.
                </b>
              </li>
            </ol>
          </li>
          <li>
            <b>
              <u>Limitation of Liability.</u>
            </b>
            <ol type="a">
              <li>
                IN NO EVENT SHALL EITHER PARTY (INCLUDING VOCOLLECT’S LICENSORS) BE LIABLE TO THE
                OTHER PARTY OR ANY THIRD PARTY FOR SPECIAL, INDIRECT, CONSEQUENTIAL, PUNITIVE OR
                INCIDENTAL DAMAGES, INCLUDING, BUT NOT LIMITED TO, LOSS OF REVENUES AND LOSS OF
                PROFITS, WHETHER ARISING UNDER CONTRACT, WARRANTY, OR TORT (INCLUDING NEGLIGENCE OR
                STRICT LIABILITY) OR ANY OTHER THEORY OF LIABILITY ARISING OUT OF OR IN CONNECTION
                WITH THE USE OF OR INABILITY TO USE THE SOFTWARE EVEN IF SUCH PARTY HAS BEEN ADVISED
                OF THE POSSIBILITY OF SUCH DAMAGES. TO THE EXTENT PERMITTED BY APPLICABLE LAW, THESE
                LIMITATIONS AND EXCLUSIONS WILL APPLY WHETHER LIABILITY ARISES FROM BREACH OF
                CONTRACT, INDEMNITY, WARRANTY, TORT, OPERATION OF LAW, OR OTHERWISE.
              </li>
              <li>
                Regardless of whether any remedy set forth herein fails of its essential purpose or
                otherwise, neither party’s total liability hereunder, regardless of the form of the
                action, shall exceed the license fee for the applicable Software. This limitation
                shall not apply to claims based on gross negligence or willful misconduct.
              </li>
            </ol>
          </li>
          <li>
            <b>
              <u>Infringement.</u>
            </b>
            <ol type="a">
              <li>
                Subject to the provisions of this Section 8 and Section 7 above, Vocollect shall, at
                its expense, (i) defend any action brought against Licensee to the extent such
                action is based on a claim that the Software directly infringes a patent, copyright
                or trademark in the United States, and (ii) indemnify Licensee for any damages and
                costs finally awarded against Licensee in such action specifically attributable to
                such claim, provided Licensee: (a) promptly notifies Vocollect of the claim in
                writing, with any delay in notice relieving Vocollect of these obligations only if
                and to the extent that Vocollect is prejudiced in its ability to carry out those
                obligations; (b) promptly furnishes to Vocollect a copy of each communication,
                notice or other action relating to the claim; (c) agrees to give Vocollect sole and
                complete authority to defend, settle, or otherwise dispose of the claim; and (d)
                gives Vocollect, at Vocollect’s expense, information and assistance necessary to
                defend or otherwise dispose of the claim.
              </li>
              <li>
                If any Software becomes, or in Vocollect’s reasonable opinion is likely to become,
                the subject of a claim of infringement, then Licensee shall permit Vocollect, at its
                option and expense, either to (i) procure the right to continue using such Software
                or (ii) replace or modify such Software so that it becomes non-infringing; or if, in
                Vocollect’s reasonable opinion, neither (i) or (ii) are commercially feasible, (iii)
                terminate the license rights for such Software and give Licensee a credit therefor,
                less depreciation calculated on a flat rate basis calculated over five (5) years
                from the date of original delivery of such Software.
              </li>
              <li>
                Because Vocollect has exclusive control of resolving claims under Section 8.a.,
                Vocollect will have no liability for any attorney fees or costs incurred by Licensee
                other than fees and costs reasonably incurred prior to invoking Vocollect’s
                obligations hereunder. Licensee shall not incur any other costs or expenses for the
                account of Vocollect under or pursuant to Sections 8.a. or 8.b. without Vocollect’s
                express prior written consent, and Vocollect shall not be liable to indemnify
                Licensee for payment of any damages or costs in any settlement unless Vocollect has
                consented to such settlement beforehand in writing.
              </li>
              <li>
                <b>
                  THE FOREGOING STATES THE ENTIRE LIABILITY OF VOCOLLECT FOR INFRINGEMENT CLAIMS AND
                  ACTIONS.
                </b>
              </li>
              <li>
                <p>
                  <b>EXCEPTIONS.</b> Any responsibility of Vocollect to Licensee regarding an action
                  or claim alleging infringement shall be reduced if, and to the extent that, it
                  arises from: (1) use of Software outside its intended purpose or, if applicable,
                  other than as specified by Vocollect; (2) use of Software in combination with
                  hardware or software not supplied by Vocollect; (3) modification of the Software
                  other than by Vocollect; or (4) designs, specifications, or similar requirements
                  provided by Licensee or at its direction, including, modification requests
                  (regardless of whether such modification occurs before or after the Software is
                  originally shipped by Vocollect to Licensee).
                </p>
                <p>
                  If an infringement action or claim is brought against Vocollect based on any of
                  these exceptions, Licensee shall, at its own expense, defend such action or claim
                  and indemnify Vocollect for any damages and costs finally awarded against
                  Vocollect in connection with such action or claim, provided that Vocollect: (a)
                  promptly notifies Licensee of the claim in writing, with any delay in notice
                  relieving Licensee of these obligations only if and to the extent that Licensee is
                  prejudiced in its ability to carry out those obligations; (b) promptly furnishes
                  to Licensee a copy of each communication, notice or other action relating to the
                  claim; (c) agrees to give Licensee sole and complete authority to defend, settle,
                  or otherwise dispose of the claim; and (d) gives Licensee, at Licensee’s expense,
                  information and assistance necessary to defend or otherwise dispose of the claim.
                </p>
                <p>
                  Because Licensee has exclusive control of resolving claims under this Section
                  8.e., Licensee will have no liability for any attorney fees or costs incurred by
                  Vocollect other than fees and costs reasonably incurred prior to invoking
                  Licensee’s obligations hereunder. Vocollect shall not incur any other costs or
                  expenses for the account of Licensee under or pursuant to this Section 8.e.
                  without Licensee’s express prior written consent, and Licensee shall not be liable
                  to indemnify Vocollect for payment of any damages or costs in any settlement
                  unless Licensee has consented to such settlement beforehand in writing.
                </p>
                <p>
                  Notwithstanding the absence of any such obligation(s), Vocollect reserves the
                  option, in its sole discretion and at its expense, to assume at any time defense
                  of any such claim.
                </p>
              </li>
            </ol>
          </li>
          <li>
            <b>
              <u>General.</u>
            </b>
            <ol type="a">
              <li>
                <b>Import/Export Compliance.</b> Licensee acknowledges that the Software is subject
                to the laws and export regulations of the United States and may be subject to export
                or import laws and regulations in other countries, and agrees to strictly comply
                with all such laws and regulations. Licensee will obtain any import, export,
                re-export approvals and licenses required for the Software and will retain
                documentation to support compliance with those laws and regulations.
              </li>
              <li>
                <b>Entire Agreement.</b> This Agreement states the entire agreement between
                Vocollect and Licensee regarding the subject matter of the Agreement, and supersedes
                all prior written and oral communications relating there. Any terms and conditions
                of a purchase order or other document issued by Licensee in connection with the
                order of the Software shall be superseded by the terms and conditions of this
                Agreement. This Agreement may be amended only by a written agreement executed by
                Licensee and Vocollect.
              </li>
              <li>
                <b>Non-Waiver.</b> The failure of Vocollect to require performance of any provision
                hereof shall in no manner affect the right at a later time to enforce such
                provision.
              </li>
              <li>
                <b>Choice of Law and Forum.</b> This Agreement shall be construed in accordance with
                the laws of the State of Delaware, United States, without regard to its conflict of
                laws provisions. Application of the Uniform Computer Information Transactions Act
                and United Nations Convention on Contracts for the International Sale of Goods,
                1980, and any successor law to either is specifically excluded. The state and
                federal courts within Delaware will have exclusive jurisdiction to adjudicate any
                dispute arising out of or related to this Agreement. Licensee consents to the
                personal jurisdiction of the above courts.
              </li>
              <li>
                <b>Assignment.</b> Neither party may assign or transfer its rights under this
                Agreement without the prior written consent of the other party, which will not be
                unreasonably withheld. However, consent shall not be required for Vocollect to
                assign or transfer its rights under this Agreement to another wholly-owned
                subsidiary of Honeywell International Inc. This Agreement shall be binding on and
                inure to the benefit of Licensee, its successors, permitted assigns and legal
                representatives.
              </li>
              <li>
                <b>References and Publicity.</b> Licensee agrees to serve as a reference for
                Vocollect. Specifically, Licensee agrees that: (a) Vocollect and Honeywell may use
                Licensee’s name and marks on their websites and marketing materials; (b) Vocollect
                may publicly announce that Licensee is a reference account; (c) Licensee will
                cooperate with Vocollect in preparing and issuing a press release regarding the
                nature of its relationship with Vocollect within Vocollect SLA US – Rev 9/26/19 6
                ninety (90) days of the execution of this agreement; (d) Licensee will participate
                in up to three (3) reference calls each calendar year with potential customers of
                Vocollect or provide Vocollect with up to three (3) reference letters describing the
                Licensee’s relationship with Vocollect and the benefits it has derived from
                Vocollect’s products and services.
              </li>
              <li>
                <b>Survival.</b> Sections 3, 4, 6.d., 7, and 9 shall survive any termination or
                expiration of this Agreement.
              </li>
              <li>
                <b>Government End Users.</b> The Software and Documentation are “Commercial Items”,
                as that term is defined at 48 C.F.R. §2.101, consisting of “Commercial Computer
                Software” and “Commercial Computer Software Documentation”, as such terms are used
                in 48 C.F.R. §12.212 or 48 C.F.R. §227.7202, as applicable. Consistent with 48
                C.F.R. §12.212 or 48 C.F.R. §227.7202-1 through 227.7202-4, as applicable, the
                Commercial Computer Software and Commercial Computer Software Documentation are
                being licensed to U.S. Government end users (a) only as Commercial Items and (b)
                with only those rights as are granted to all other end users pursuant to the terms
                and conditions herein.
              </li>
            </ol>
          </li>
        </ol>
        <br />
        <center>
          <b>EXHIBIT A</b>
        </center>
        <br />
        <center>
          <b>Affiliates</b>
        </center>
        <p>Please list any Affiliates included under Section 2.b.</p>
        <br />
        <center>
          <b>EXHIBIT B</b>
        </center>
        <br />
        <center>
          <b>Vocollect’s Data Privacy Obligations for the provision of Software</b>
        </center>
        <br />
        <ol>
          <li>
            Vocollect may process Licensee Personal Data in relation to the Software as detailed in
            this Agreement and including in accordance with the following scope, in each case as
            further specified in an Order as necessary:
            <ul>
              <li>
                <b>Categories of Data Subjects:</b> Customer, employees and end-users of Customer.
              </li>
              <li>
                <b>Categories of data:</b> name, location information, facility, device or equipment
                usage data.
              </li>
            </ul>
          </li>
          <br />
          <li>
            To the extent the laws of a jurisdiction recognize the roles of “data controller” and
            “data processor” as applied to Personal Data then, as between Customer and Vocollect,
            Customer acts as data controller and Vocollect acts as data processor and shall process
            Licensee Personal Data solely on behalf of and in accordance with Customer’s documented
            instructions, the Agreement and Applicable Data Privacy Laws and only to the extent, and
            for so long as necessary, to provide, protect, improve or develop the Software and/or
            related services and perform rights and obligations under the Agreement. Both Parties
            shall comply with their obligations under Applicable Data Privacy Laws including in
            their respective roles as controller and processor of Licensee Personal Data.
          </li>
          <br />
          <li>
            Vocollect shall have no liability for any losses, costs, expenses or liabilities arising
            from or in connection with processing of Licensee Personal Data in compliance with the
            Agreement or otherwise in compliance with Customer´s written instructions.
          </li>
          <br />
          <li>
            Vocollect shall refer all data subject requests to exercise rights under Applicable Data
            Privacy Laws to Customer and provide reasonable assistance to enable Customer to comply
            with such requests, enable Personal Data security, respond to complaints or inquiries
            and to conduct any privacy impact assessments, provided Customer reimburse all
            reasonably incurred costs.
          </li>
          <br />
          <li>
            Upon termination Vocollect shall delete or anonymize all Licensee Personal Data, except
            Vocollect may retain Licensee Personal Data if required or permitted by applicable law
            for compliance, audit or security purposes.
          </li>
          <br />
          <li>
            If Vocollect processes Licensee Personal Data relating to data subjects in the European
            Economic Area (“EEA”), Switzerland or Philippines: (i) if Vocollect believes any
            instruction from Customer will violate Applicable Data Privacy Laws, or if applicable
            law requires Vocollect to process Licensee Personal Data relating to data subjects in
            the EEA in a way that is not in line with Customer´s documented instructions, Vocollect
            shall notify Customer in writing, unless the law prohibits such notification on
            important grounds of public interest; (ii) Vocollect shall upon request make available
            the identity of sub-processors and notify any intended addition or replacement and
            Customer shall have 5 business days to object. If Customer objects and the parties do
            not resolve within 1 month, Vocollect may terminate without penalty on written notice;
            and (iii) Vocollect shall ensure personnel processing Licensee Personal Data of EEA data
            subjects have committed to confidentiality in relation to such processing.
          </li>
          <br />
          <li>
            Customer authorizes Vocollect to share Licensee Personal Data with sub-processors
            (including Affiliates and service providers) located in any jurisdiction in connection
            with the Agreement, provided Vocollect uses legally enforceable transfer mechanisms and
            contractually requires sub-processors to abide by terms no less restrictive that those
            in the Agreement with regards to processing of Licensee Personal Data.
          </li>
          <br />
          <li>
            Where transfers of Licensee Personal Data require: (i) Customer authorizes Vocollect and
            Vocollect Affiliates to act as agent for the limited purpose of binding Customer as
            principal, in the capacity of “data exporter”, to a Vocollect inter-group or Vocollect
            and service provider data transfer agreement comprising the Standard Contractual Clauses
            for the transfer of personal data to processors established in third countries adopted
            by the European Commission (“SCC”); and (ii) the parties agree that the SCCs (located at
            https://ec.europa.eu/info/law/law-topic/data-protection/data-transfers-outside-eu/model-contracts-transferpersonal-data-third-countries_en
            or updated more recent website) shall be deemed to have been signed by Customer and
            Customer´s affiliates, in the capacity of “data exporter”, and by Vocollect and/or
            Vocollect´s Vocollect SLA US – Rev 9/26/19 9 affiliates, in the capacity of “data
            importer” and the information required to be provided in the SCCs’ appendices shall be
            as described in this Exhibit or separately agreed in writing.
          </li>
          <br />
          <li>
            Security is governed by the policies as further specified in the Order. If no additional
            Security Policy is specified in the Order, Vocollect will use reasonable administrative,
            physical and technical safeguards to protect Personal Data and Input Data and follow
            industry-standard security practices. Customer will implement reasonable administrative,
            physical and technical safeguards to protect the Software and follow industry-standard
            security practices. Customer is solely responsible for costs incurred due to
            unauthorized use or access through Customer´s account credentials or systems.
          </li>
          <br />
          <li>
            Customer may audit Vocollect’s compliance with this Section once per year, or more
            frequently if applicable laws require. Audits will occur following Customer’s written
            request at least 90 days prior to the proposed start date and Customer providing a
            reasonably detailed audit plan describing the proposed scope, start date and duration,
            unless otherwise required by Applicable Data Privacy Laws. The Parties will work in good
            faith to agree on a final audit plan. Each Party will bear their own costs related to
            the audit. The audit will be conducted during Vocollect regular business hours at the
            applicable facility, subject to the published policies of that facility, and may not
            unreasonably interfere with business activities. If a third party is to conduct the
            audit they must execute a written confidentiality agreement acceptable to Vocollect. If
            the information required for an audit is not contained in existing reports, Vocollect
            will make reasonable efforts to provide it to the auditor. To preserve the security of
            Vocollect customers and organization Vocollect reserves the right to not share
            information that could expose or compromise its security, privacy, employment policies
            or obligations to other customers or third parties or share Confidential Information.
            Records may not be copied or removed from Vocollect or Vocollect affiliates’ facilities.
            Customer will generate and provide Vocollect with an audit report within 3 months of
            audit end, unless prohibited by law. Audit reports are Vocollect Confidential
            Information and may only be used for the purposes of meeting Customer’s regulatory
            requirements or confirming Vocollect’s compliance with this Exhibit.
          </li>
          <br />
          <li>
            Vocollect shall evaluate and respond to any confirmed breach of security leading to the
            accidental or unlawful destruction, loss, alteration or unauthorized access, disclosure
            or use of the Software and/or of Licensee Personal Data due to a breach of Vocollect’s
            obligations under this Exhibit (each a <b>“Security Incident”</b>
            ), and will work with Customer (and where necessary with outside regulatory and law
            enforcement authorities) to develop response strategies and respond to and mitigate the
            adverse effects of a Security Incident. Where it is determined a Security Incident has
            occurred, Vocollect shall notify Customer without undue delay and as relevant
            information becomes available to assist Customer in meeting its potential reporting or
            notice obligations under Applicable Data Privacy Laws. Vocollect shall include a
            description, whether and what type of Licensee Personal Data may have been affected and
            such information as Vocollect may reasonably request, unless the law prohibits it.
            Customer shall work with Vocollect in good faith to develop any related public
            statements or required notices resulting from a Security Incident. Provided Vocollect is
            in material compliance with its obligations under this Exhibit, Vocollect’s obligations
            set out in this Exhibit are Vocollect’s sole obligations, and Customer’s sole and
            exclusive remedy, for Security Incidents.
          </li>
        </ol>
      </>
    );
  }
}

export default SLAText;
